import {
    ArrayItemPropertyDef,
    ServiceTemplateConfigType,
    ServiceTemplateField,
    TemplateValidationError,
} from './service-template.model';

export type TemplateFormData = Record<string, Record<string, any>>;

/** A ready-to-POST config body (configTypeId + name + data). */
export interface ConfigPayload {
    configTypeId: string;
    name: string;
    data: Record<string, any>;
    /** Optional OpenZiti tags stored on the created config (e.g. to mark the workflow that made it). */
    tags?: Record<string, any>;
}

export function buildInitialFormData(configTypes: ServiceTemplateConfigType[]): TemplateFormData {
    const formData: TemplateFormData = {};
    configTypes.forEach((ct) => {
        formData[ct.configTypeId] = {};
        ct.fields.forEach((field) => {
            if (field.defaultValue !== undefined && field.defaultValue !== null && field.defaultValue !== '') {
                formData[ct.configTypeId][field.fieldPath] = field.defaultValue;
            } else if (field.type === 'array') {
                formData[ct.configTypeId][field.fieldPath] = [];
            } else if (field.type === 'boolean') {
                // Seed a concrete false so the toggle's "NO" matches the stored value
                // (an unseeded boolean is undefined → reads as "NO" yet fails required).
                formData[ct.configTypeId][field.fieldPath] = false;
            }
        });
    });
    return formData;
}

export function isPortRangeField(field: {
    type: string;
    arrayItemType?: string;
    arrayItemProperties?: Record<string, ArrayItemPropertyDef>;
}): boolean {
    if (field.type !== 'array' || field.arrayItemType !== 'object' || !field.arrayItemProperties) {
        return false;
    }
    const keys = Object.keys(field.arrayItemProperties).sort();
    return keys.length === 2 && keys[0] === 'high' && keys[1] === 'low';
}

export function formatPortRangeChips(ranges: Array<{ low: number; high: number }> | undefined): string[] {
    if (!ranges) {
        return [];
    }
    return ranges.map((range) => (range.low === range.high ? `${range.low}` : `${range.low}-${range.high}`));
}

export function parsePortRangeChips(chips: string[] | undefined): Array<{ low: number; high: number }> {
    if (!chips) {
        return [];
    }
    const ranges: Array<{ low: number; high: number }> = [];
    chips.forEach((chip) => {
        const parts = chip.split('-');
        if (parts.length === 1) {
            const port = parseInt(parts[0], 10);
            if (!isNaN(port)) {
                ranges.push({ low: port, high: port });
            }
        } else if (parts.length === 2) {
            const low = parseInt(parts[0], 10);
            const high = parseInt(parts[1], 10);
            if (!isNaN(low) && !isNaN(high)) {
                ranges.push({ low, high });
            }
        }
    });
    return ranges;
}

export function isValueEmpty(value: any, fieldType: string): boolean {
    if (value === undefined || value === null) {
        return true;
    }
    // An empty string is empty for any scalar type (string/number/integer/boolean),
    // not just strings — otherwise a cleared number field reads as "filled".
    if (value === '') {
        return true;
    }
    if (fieldType === 'array' && Array.isArray(value) && value.length === 0) {
        return true;
    }
    return false;
}

export function validateTemplateForm(
    configTypes: ServiceTemplateConfigType[],
    formData: TemplateFormData,
    schemaRequiredByConfigType: Record<string, string[]> = {}
): TemplateValidationError[] {
    const errors: TemplateValidationError[] = [];

    configTypes.forEach((ct) => {
        const schemaRequired = schemaRequiredByConfigType[ct.configTypeId] || [];

        ct.fields.forEach((field) => {
            const value = formData[ct.configTypeId]?.[field.fieldPath];
            const isRequired = field.required || schemaRequired.includes(field.fieldPath);

            if (isRequired && isValueEmpty(value, field.type)) {
                errors.push({
                    configType: ct.configTypeId,
                    field: field.fieldPath,
                    message: `${field.label} is required`,
                });
                return;
            }

            // A field only triggers its dependents / type check when it actually
            // carries a value. A boolean that's off (false) counts as "not set"
            // here so an OFF toggle doesn't force its dependent fields to be filled.
            if (isValueEmpty(value, field.type) || (field.type === 'boolean' && value !== true)) {
                return;
            }

            if (field.dependentOf && field.dependentOf.length > 0) {
                field.dependentOf.forEach((depFieldPath) => {
                    const depField = ct.fields.find((f) => f.fieldPath === depFieldPath);
                    if (depField) {
                        const depValue = formData[ct.configTypeId]?.[depFieldPath];
                        if (isValueEmpty(depValue, depField.type)) {
                            errors.push({
                                configType: ct.configTypeId,
                                field: depFieldPath,
                                message: `${depField.label} is required when ${field.label} is set`,
                            });
                        }
                    }
                });
            }

            const typeError = validateFieldType(value, field);
            if (typeError) {
                errors.push({ configType: ct.configTypeId, field: field.fieldPath, message: typeError });
            }
        });
    });

    return errors;
}

function validateFieldType(value: any, field: ServiceTemplateField): string | null {
    switch (field.type) {
        case 'string':
            if (typeof value !== 'string') {
                return `${field.label} must be a string`;
            }
            break;
        case 'integer': {
            const intVal = Number(value);
            if (isNaN(intVal) || !Number.isInteger(intVal)) {
                return `${field.label} must be a whole number`;
            }
            break;
        }
        case 'number': {
            const numVal = Number(value);
            if (isNaN(numVal)) {
                return `${field.label} must be a number`;
            }
            break;
        }
        case 'boolean':
            if (typeof value !== 'boolean') {
                return `${field.label} must be true or false`;
            }
            break;
        case 'array':
            if (!Array.isArray(value)) {
                return `${field.label} must be an array`;
            }
            for (let i = 0; i < value.length; i++) {
                const item = value[i];
                if (field.arrayItemType === 'integer') {
                    const itemVal = Number(item);
                    if (isNaN(itemVal) || !Number.isInteger(itemVal)) {
                        return `${field.label} item ${i + 1} must be a whole number`;
                    }
                } else if (field.arrayItemType === 'number') {
                    const itemVal = Number(item);
                    if (isNaN(itemVal)) {
                        return `${field.label} item ${i + 1} must be a number`;
                    }
                } else if (field.arrayItemType === 'object' && field.arrayItemProperties) {
                    if (typeof item !== 'object') {
                        return `${field.label} item ${i + 1} must be an object`;
                    }
                    for (const [propKey, propDef] of Object.entries(field.arrayItemProperties)) {
                        const propValue = item[propKey];
                        if (propValue !== undefined && propValue !== null && propValue !== '') {
                            if (propDef.type === 'integer' || propDef.type === 'number') {
                                const pVal = Number(propValue);
                                if (isNaN(pVal) || (propDef.type === 'integer' && !Number.isInteger(pVal))) {
                                    return `${field.label} item ${i + 1}: ${propKey} must be a ${
                                        propDef.type === 'integer' ? 'whole number' : 'number'
                                    }`;
                                }
                            }
                        }
                    }
                }
            }
            break;
    }
    return null;
}

export function convertValueByType(value: any, type: string): any {
    switch (type) {
        case 'integer':
        case 'number': {
            const num = Number(value);
            return isNaN(num) ? value : num;
        }
        case 'boolean':
            if (typeof value === 'boolean') {
                return value;
            }
            return value === 'true' || value === true;
        case 'string':
        default:
            return String(value);
    }
}

export function buildConfigPayloads(
    configTypes: ServiceTemplateConfigType[],
    formData: TemplateFormData,
    serviceName: string
): ConfigPayload[] {
    return configTypes.map((ct) => {
        const configData: Record<string, any> = {};

        ct.fields.forEach((field) => {
            const value = formData[ct.configTypeId]?.[field.fieldPath];
            // Include a field only when it actually holds a value. A cleared scalar
            // ('' / undefined / null) and an empty array are skipped rather than
            // coerced — so an emptied number field is omitted, not sent as 0.
            if (isValueEmpty(value, field.type)) {
                return;
            }

            if (Array.isArray(value)) {
                if (value.length > 0) {
                    configData[field.fieldPath] = value.map((item) => {
                        if (typeof item === 'object' && field.arrayItemProperties) {
                            const convertedItem: Record<string, any> = {};
                            for (const [propKey, propDef] of Object.entries(field.arrayItemProperties)) {
                                const propValue = item[propKey];
                                const isEmpty =
                                    propValue === undefined ||
                                    propValue === null ||
                                    (propValue === '' && propDef.type !== 'integer' && propDef.type !== 'number');
                                if (!isEmpty) {
                                    convertedItem[propKey] = convertValueByType(propValue, propDef.type);
                                }
                            }
                            return convertedItem;
                        }
                        return convertValueByType(item, field.arrayItemType || 'string');
                    });
                }
            } else {
                configData[field.fieldPath] = convertValueByType(value, field.type);
            }
        });

        return {
            configTypeId: ct.configTypeId,
            name: `${serviceName}-${ct.configTypeName}`,
            data: configData,
        };
    });
}
